import React from "react";
import { Card, Alert } from "react-bootstrap";

function AssigningPermissions() {
  return (
    <div>
      <h2 className="mb-4">
        Assigning Permissions
      </h2>

      <Alert variant="info">
        Learn how to assign permissions to groups to control what actions group members can perform.
      </Alert>

      <Card className="mb-4">
        <Card.Body>
          <h4>What Are Permissions?</h4>
          <p>
            Permissions define what actions users can perform in the portal. For example:
          </p>
          <ul>
            <li>Can they execute runbooks?</li>
            <li>Can they create new runbooks?</li>
            <li>Can they edit schedules?</li>
            <li>Can they view execution history?</li>
          </ul>
          <p className="mt-3">
            By assigning permissions to a group, all members of that group gain those abilities.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Step-by-Step: Assigning Permissions</h4>
          <ol>
            <li>Go to <strong>Permissions</strong> in the Settings section</li>
            <li>Click on a permission group in the table to select it</li>
            <li>Click the <strong>Manage Permissions</strong> button</li>
            <li>A modal will open showing all available permissions organized by category</li>
            <li>Check the boxes next to permissions you want to grant to this group</li>
            <li>Uncheck any permissions you want to remove</li>
            <li>Changes are saved automatically as you check/uncheck boxes</li>
          </ol>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Available Permission Categories</h4>

          <h6 className="mt-3">Runbook Management</h6>
          <ul>
            <li><code>runbooks.create</code> - Create new runbooks</li>
            <li><code>runbooks.edit</code> - Edit existing runbooks</li>
            <li><code>runbooks.delete</code> - Delete runbooks</li>
            <li><code>runbooks.execute</code> - Run runbooks manually</li>
            <li><code>runbooks.validate</code> - Validate runbook configuration</li>
            <li><code>runbooks.favorite</code> - Mark runbooks as favorites</li>
          </ul>

          <h6 className="mt-3">Schedule Management</h6>
          <ul>
            <li><code>schedules.view</code> - View scheduled runbooks</li>
            <li><code>schedules.create</code> - Create runbook schedules</li>
            <li><code>schedules.edit</code> - Edit runbook schedules</li>
            <li><code>schedules.delete</code> - Delete runbook schedules</li>
          </ul>

          <h6 className="mt-3">Variable Management</h6>
          <ul>
            <li><code>variables.view</code> - View variables</li>
            <li><code>variables.create</code> - Create variable data</li>
            <li><code>variables.edit</code> - Edit variable data</li>
            <li><code>variables.delete</code> - Delete variable data</li>
          </ul>

          <h6 className="mt-3">Connector Management</h6>
          <ul>
            <li><code>connectors.view</code> - View connectors</li>
            <li><code>connectors.edit</code> - Edit connectors</li>
            <li><code>connectors.editSecrets</code> - Edit connector credentials</li>
          </ul>
          <p className="mt-2">
            <strong>Note:</strong> Creating connectors is restricted to Admin and Super User roles only.
          </p>

          <h6 className="mt-3">Execution History</h6>
          <ul>
            <li><code>history.view</code> - View runbook execution history</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Common Permission Sets</h4>

          <h6 className="mt-3">Read-Only Access</h6>
          <p><strong>For users who only need to view information:</strong></p>
          <ul>
            <li>variables.view</li>
            <li>connectors.view</li>
            <li>schedules.view</li>
            <li>history.view</li>
          </ul>

          <h6 className="mt-3">Operator Access</h6>
          <p><strong>For users who execute but don't modify:</strong></p>
          <ul>
            <li>runbooks.execute</li>
            <li>runbooks.favorite</li>
            <li>schedules.view, create, edit, delete (full schedule control)</li>
            <li>variables.view</li>
            <li>connectors.view</li>
            <li>history.view</li>
          </ul>

          <h6 className="mt-3">Developer Access</h6>
          <p><strong>For users who create and manage workflows:</strong></p>
          <ul>
            <li>All runbook permissions</li>
            <li>All schedule permissions</li>
            <li>All variable permissions</li>
            <li>All connector permissions (including editSecrets)</li>
            <li>history.view</li>
          </ul>

          <h6 className="mt-3">Scheduler Manager</h6>
          <p><strong>For users who only manage when things run:</strong></p>
          <ul>
            <li>runbooks.execute</li>
            <li>schedules.view, create, edit, delete</li>
            <li>history.view</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Best Practices</h4>
          <ul>
            <li><strong>Start Minimal</strong> - Begin with fewer permissions and add more as needed</li>
            <li><strong>Group Related Permissions</strong> - If they need to edit runbooks, they probably also need to validate them</li>
            <li><strong>Consider the Workflow</strong> - Think about what users need to complete their job, not just one action</li>
            <li><strong>Test the Experience</strong> - After assigning permissions, verify users can complete their tasks</li>
            <li><strong>Document Your Choices</strong> - Use the group description to note why certain permissions were granted</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Understanding Permission Dependencies</h4>
          <p>Some actions require multiple permissions to be useful:</p>

          <h6 className="mt-3">To Execute Runbooks Effectively:</h6>
          <ul>
            <li><code>runbooks.execute</code> - To run the runbook</li>
            <li><code>history.view</code> - To see if it worked</li>
            <li><code>variables.view</code> and <code>connectors.view</code> - To troubleshoot issues</li>
          </ul>

          <h6 className="mt-3">To Create Runbooks Effectively:</h6>
          <ul>
            <li><code>runbooks.create</code> and <code>runbooks.edit</code> - To build the workflow</li>
            <li><code>runbooks.validate</code> - To check for errors</li>
            <li><code>runbooks.execute</code> - To test it</li>
            <li><code>variables.*</code> and <code>connectors.*</code> - To configure data and connections</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Modifying Permissions</h4>
          <p>
            You can change permissions at any time:
          </p>
          <ol>
            <li>Select the group</li>
            <li>Click <strong>Manage Permissions</strong></li>
            <li>Check or uncheck permissions</li>
            <li>Changes take effect immediately - users don't need to log out</li>
          </ol>
          <Alert variant="info" className="mt-3">
            <strong>Tip:</strong> If you're unsure about a permission, you can temporarily grant it, let the user try their task,
            and then remove it if it wasn't needed.
          </Alert>
        </Card.Body>
      </Card>
    </div>
  );
}

export default AssigningPermissions;
